import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ApiCallError, callFunction } from '@/lib/api';
import { CORRESPONDENCE_DEFAULT_LIMIT } from '@/lib/medcom/ctlPreviewQuery';
import { shouldSkipCorrespondenceAutoRefresh } from '@/lib/medcom/correspondencePanel';
import { Xdis91AnswerForm } from '@/components/xdis/Xdis91AnswerForm';
import { formatDanishDateTimeSeconds, formatDanishTime } from '@/lib/danishDateTime';

// C10 (BIN01-R): received XDIS91 korrespondancer with their XBIN01 bilag,
// coupled on ObjectIdentifier by communicate-correspondence. No CPR is returned.
// Spor A block 3 (T3.2): 'Besvar' answers an XDIS91 within its episode (communicate-xdis).

type Attachment = {
  object_identifier: string;
  object_code: string | null;
  object_extension_code: string | null;
  original_object_size: number | null;
  received: boolean;
  exchange_id: string | null;
  received_at: string | null;
  size_matches: boolean | null;
};

type Message = {
  exchange_id: string;
  received_at: string | null;
  envelope_identifier: string | null;
  sent: string | null;
  sender: { ean: string | null; organisation: string | null; department: string | null; unit: string | null };
  patient_name: string | null;
  priority: string | null;
  subject: string | null;
  text: string[];
  attachments: Attachment[];
};

// BIN01-M 3.3: an XBIN01 received before its XDIS91. Listed on its own with
// 'Afventer meddelelse' until an XDIS91 cites it; then it moves under that message.
type WaitingMedbin = {
  exchange_id: string;
  kind: string;
  received_at: string | null;
  sender_ean: string | null;
  object_identifier: string;
  object_code: string | null;
  object_extension_code: string | null;
  original_object_size: number | null;
  size_matches: boolean | null;
};

const AUTO_REFRESH_MS = 30_000;

function formatBytes(size: number | null): string | null {
  if (size === null || !Number.isFinite(size)) return null;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1).replace('.', ',')} KB`;
  return `${(size / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

function formatTime(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? iso
    : formatDanishDateTimeSeconds(date);
}

type Download = { file_name: string; mime_type: string; size: number; base64: string };

function saveBase64(download: Download) {
  const binary = atob(download.base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  const url = URL.createObjectURL(new Blob([bytes], { type: download.mime_type }));
  const link = document.createElement('a');
  link.href = url;
  link.download = download.file_name;
  link.click();
  URL.revokeObjectURL(url);
}

export function ReceivedCorrespondencePanel({ clinicId, refreshKey }: { clinicId: string; refreshKey?: number }) {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<Message[] | null>(null);
  const [waiting, setWaiting] = useState<WaitingMedbin[]>([]);
  const [loadedAt, setLoadedAt] = useState<Date | null>(null);
  const requestSeq = useRef(0);
  const offsetRef = useRef(0);
  const inFlightRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  /** Spor A block 3, T3.2: the XDIS91 being answered within its episode. */
  const [answering, setAnswering] = useState<string | null>(null);
  const [draftSearch, setDraftSearch] = useState('');
  const [draftKind, setDraftKind] = useState('');
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState('');
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  // Every call gets a sequence number so a slow, older response can never
  // overwrite a newer list (C10: 'Opdatér' must show a new XDIS91 at once).
  const load = useCallback(async (nextOffset = 0, append = false) => {
    inFlightRef.current = true;
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    try {
      const result = await callFunction<
        {
          action: 'list';
          clinic_id: string;
          nonce: number;
          limit?: number;
          offset?: number;
          search?: string;
          kind?: string;
        },
        { messages: Message[]; waiting_medbins?: WaitingMedbin[]; has_more?: boolean }
      >('communicate-correspondence', {
        action: 'list',
        clinic_id: clinicId,
        nonce: Date.now(),
        limit: CORRESPONDENCE_DEFAULT_LIMIT,
        offset: nextOffset,
        search: search.trim() || undefined,
        kind: kind.trim() || undefined,
      });
      if (seq !== requestSeq.current) return;
      setMessages((current) =>
        append && current ? [...current, ...result.messages] : [...result.messages],
      );
      setWaiting((current) => (append ? current : [...(result.waiting_medbins ?? [])]));
      setHasMore(result.has_more === true);
      setOffset(nextOffset);
      offsetRef.current = nextOffset;
      setLoadedAt(new Date());
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setError(err instanceof ApiCallError ? err.message : t('medcom.correspondence.load_failed', 'Korrespondancer kunne ikke hentes.'));
    } finally {
      if (seq === requestSeq.current) {
        inFlightRef.current = false;
        setLoading(false);
      }
    }
  }, [clinicId, t, search, kind]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      if (shouldSkipCorrespondenceAutoRefresh({
        offset: offsetRef.current,
        requestInFlight: inFlightRef.current,
      })) return;
      void load(0, false);
    }, AUTO_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  async function onDownload(message: Message, attachment: Attachment) {
    const key = `${message.exchange_id}:${attachment.object_identifier}`;
    setDownloading(key);
    setError(null);
    try {
      const result = await callFunction<Record<string, string>, Download>('communicate-correspondence', {
        action: 'download',
        clinic_id: clinicId,
        message_exchange_id: message.exchange_id,
        object_identifier: attachment.object_identifier,
      });
      saveBase64(result);
    } catch (err) {
      setError(err instanceof ApiCallError ? err.message : t('medcom.correspondence.download_failed', 'Bilaget kunne ikke hentes.'));
    } finally {
      setDownloading(null);
    }
  }

  return (
    <section className="space-y-3 rounded-md border border-ds-line p-4" data-testid="received-correspondence">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">
          {t('medcom.correspondence.title', 'Modtagne korrespondancer (XDIS91 + XBIN01)')}
        </h3>
        <div className="flex items-center gap-2">
          {loadedAt && (
            <span className="text-xs text-ds-muted" data-testid="correspondence-loaded-at">
              {t('medcom.correspondence.loaded_at', 'Opdateret')} {formatDanishTime(loadedAt)}
            </span>
          )}
          <Button type="button" variant="secondary" size="sm" onClick={() => void load(0, false)} disabled={loading}>
            {loading ? t('medcom.correspondence.refreshing', 'Opdaterer…') : t('medcom.correspondence.refresh', 'Opdatér')}
          </Button>
        </div>
      </div>
      <form
        className="flex flex-wrap items-end gap-2"
        data-testid="correspondence-filters"
        onSubmit={(event) => {
          event.preventDefault();
          setSearch(draftSearch);
          setKind(draftKind);
          setOffset(0);
        }}
      >
        <Input
          label={t('medcom.correspondence.search', 'Søg kuvert eller brev')}
          name="correspondence-search"
          value={draftSearch}
          onChange={(event) => setDraftSearch(event.target.value)}
          placeholder={t('medcom.correspondence.search_placeholder', 'Fx s2b1m31e1710')}
        />
        <Input
          label={t('medcom.correspondence.type_filter', 'Meddelelsestype')}
          name="correspondence-kind"
          value={draftKind}
          onChange={(event) => setDraftKind(event.target.value)}
          placeholder={t('medcom.correspondence.type_all', 'Alle (XDIS91)')}
        />
        <Button type="submit" variant="secondary" size="sm">
          {t('medcom.correspondence.search_submit', 'Søg')}
        </Button>
      </form>
      {error && (
        <p role="alert" className="text-sm text-ds-red-ink">
          {error}
        </p>
      )}
      {waiting.map((medbin) => (
        <article
          key={`waiting:${medbin.exchange_id}:${medbin.object_identifier}`}
          className="space-y-1 rounded-md border border-ds-amber-line bg-ds-amber-tint p-3 text-sm"
          data-testid="correspondence-waiting-medbin"
        >
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="font-medium">
              {medbin.kind} {t('medcom.correspondence.orphan_title', 'uden meddelelse')}
            </span>
            <span className="text-xs font-semibold text-ds-amber-ink">
              {t('medcom.correspondence.awaiting_message', 'Afventer meddelelse')}
            </span>
          </div>
          <p className="text-xs text-ds-text">
            {t('medcom.correspondence.sender_ean', 'Afsender-EAN')}: {medbin.sender_ean ?? '–'}
            {' · '}
            {t('medcom.correspondence.size', 'Størrelse')}: {formatBytes(medbin.original_object_size) ?? '–'}
            {medbin.received_at ? ` · ${t('medcom.correspondence.received', 'Modtaget')} ${formatTime(medbin.received_at)}` : ''}
          </p>
          <p className="font-mono text-xs">
            {medbin.object_identifier}
            {medbin.object_extension_code ? `.${medbin.object_extension_code}` : ''}
          </p>
          <p className="text-xs text-ds-muted">
            {t(
              'medcom.correspondence.awaiting_message_help',
              'Bilaget er modtaget før sin XDIS91. Det kobles automatisk til korrespondancen, når en XDIS91 med samme ObjectIdentifier modtages.',
            )}
          </p>
        </article>
      ))}
      {messages && messages.length === 0 && waiting.length === 0 && (
        <p className="text-sm text-ds-muted">
          {t('medcom.correspondence.empty', 'Ingen modtagne korrespondancer.')}
        </p>
      )}
      {messages?.map((message) => (
        <article key={message.exchange_id} className="space-y-2 rounded-md bg-ds-sunk p-3 text-sm">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="font-medium">{message.subject || t('medcom.correspondence.no_subject', '(uden emne)')}</span>
            <span className="text-xs text-ds-muted">
              {message.priority === 'høj_prioritet'
                ? t('medcom.correspondence.priority_high', 'Høj prioritet')
                : t('medcom.correspondence.priority_rutine', 'Rutine')}
              {message.sent ? ` · ${message.sent}` : ''}
            </span>
          </div>
          <p className="text-xs text-ds-muted">
            {t('medcom.correspondence.from', 'Fra')}:{' '}
            {[message.sender.organisation, message.sender.department, message.sender.unit].filter(Boolean).join(', ') ||
              message.sender.ean}
            {message.patient_name ? ` · ${t('medcom.correspondence.patient', 'Patient')}: ${message.patient_name}` : ''}
          </p>
          <p className="text-xs text-ds-muted">
            {t('medcom.correspondence.sender_ean', 'Afsender-EAN')}: {message.sender.ean ?? '–'}
            {message.received_at ? ` · ${t('medcom.correspondence.received', 'Modtaget')} ${formatTime(message.received_at)}` : ''}
          </p>
          {message.text.length > 0 && (
            <pre className="whitespace-pre-wrap font-mono text-xs">{message.text.join('\n')}</pre>
          )}
          <ul className="space-y-1">
            {message.attachments.map((attachment) => {
              const key = `${message.exchange_id}:${attachment.object_identifier}`;
              return (
                <li key={key} className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="font-mono">
                    {attachment.object_identifier}
                    {attachment.object_extension_code ? `.${attachment.object_extension_code}` : ''}
                  </span>
                  <span className="text-ds-muted">
                    {formatBytes(attachment.original_object_size) ?? '–'}
                  </span>
                  {attachment.received ? (
                    <>
                      <span className="text-ds-green-ink">
                        {t('medcom.correspondence.attachment_received', 'Modtaget')}
                      </span>
                      {attachment.size_matches === false && (
                        <span className="text-ds-amber-ink">
                          {t('medcom.correspondence.size_mismatch', 'Størrelsen passer ikke')}
                        </span>
                      )}
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        disabled={downloading === key}
                        onClick={() => void onDownload(message, attachment)}
                      >
                        {t('medcom.correspondence.download', 'Hent bilag')}
                      </Button>
                    </>
                  ) : (
                    <span className="text-ds-amber-ink">
                      {t('medcom.correspondence.attachment_waiting', 'Afventer bilag')}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
          {answering === message.exchange_id ? (
            <Xdis91AnswerForm
              clinicId={clinicId}
              exchangeId={message.exchange_id}
              subject={message.subject}
              onClose={() => setAnswering(null)}
            />
          ) : (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setAnswering(message.exchange_id)}
              data-testid="correspondence-answer"
            >
              {t('medcom.correspondence.answer', 'Besvar')}
            </Button>
          )}
        </article>
      ))}
      {hasMore ? (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          data-testid="correspondence-older"
          disabled={loading}
          onClick={() => void load(offset + CORRESPONDENCE_DEFAULT_LIMIT, true)}
        >
          {t('medcom.correspondence.older', 'Vis ældre')}
        </Button>
      ) : null}
    </section>
  );
}
